import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { HiddenList, Links, Select } from './ResponsiveMenu.styles';

function ResponsiveMenu({
  items,
  containerWidth = null,
  getActiveLink = () => {},
  renderLink,
  renderHiddenLink,
  renderMoreBtn,
  defaultItemsCount = 2,
}) {
  const listRef = useRef(null);
  const moreLinkRef = useRef(null);

  const [state, setState] = useState(() => {
    const itemsArray = Array.isArray(items) ? items : Object.values(items);

    return {
      items: itemsArray,
      cachedItems: items,
      visibleItems: itemsArray.slice(0, defaultItemsCount),
      hiddenItems: itemsArray.slice(defaultItemsCount),
      hiddenItemsDimensions: [],
      isOpen: false,
    };
  });

  const visibleItemsRefs = useRef(new Map());
  const hiddenItemsRefs = useRef(new Map());

  // Sync state with props.items (simulate getDerivedStateFromProps)
  useEffect(() => {
    if (items !== state.cachedItems) {
      const itemsArray = Array.isArray(items) ? items : Object.values(items);
      setState((prev) => ({
        ...prev,
        items: itemsArray,
        cachedItems: items,
        visibleItems: itemsArray.slice(0, defaultItemsCount),
        hiddenItems: itemsArray.slice(defaultItemsCount),
        hiddenItemsDimensions: [],
      }));
    }
  }, [items, defaultItemsCount, state.cachedItems]);

  const updateVisibleLinksItems = useCallback(() => {
    if (!containerWidth || containerWidth <= 640) return;

    const currentWidth = listRef.current?.clientWidth || 0;
    const lastHiddenItemWidth =
      state.hiddenItemsDimensions[state.hiddenItemsDimensions.length - 1] || 0;

    if (containerWidth <= currentWidth) {
      // Need to hide a link
      if (state.visibleItems.length > 0) {
        const lastIndex = state.visibleItems.length - 1;
        const lastRef = visibleItemsRefs.current.get(lastIndex);
        const lastWidth = lastRef?.current?.clientWidth || 0;

        setState((prev) => ({
          ...prev,
          visibleItems: prev.visibleItems.slice(0, -1),
          hiddenItems: [prev.visibleItems[lastIndex], ...prev.hiddenItems],
          hiddenItemsDimensions: [...prev.hiddenItemsDimensions, lastWidth],
        }));
      }
    } else if (
      state.hiddenItems.length &&
      containerWidth > currentWidth + lastHiddenItemWidth
    ) {
      // Can show a hidden link
      setState((prev) => ({
        ...prev,
        visibleItems: [...prev.visibleItems, prev.hiddenItems[0]],
        hiddenItems: prev.hiddenItems.slice(1),
        hiddenItemsDimensions: prev.hiddenItemsDimensions.slice(0, -1),
      }));
    }
  }, [
    containerWidth,
    state.visibleItems,
    state.hiddenItems,
    state.hiddenItemsDimensions,
  ]);

  useLayoutEffect(() => {
    updateVisibleLinksItems();
  }, [updateVisibleLinksItems]);

  const toggle = useCallback((open) => {
    setState((prev) => ({
      ...prev,
      isOpen: open !== undefined ? open : !prev.isOpen,
    }));
  }, []);

  useEffect(() => {
    const handleTouch = (e) => {
      if (e.currentTarget === moreLinkRef.current) {
        toggle();
      }
    };

    window.addEventListener('touchend', handleTouch);

    return () => {
      window.removeEventListener('touchend', handleTouch);
    };
  }, []);

  const activeLink = getActiveLink(
    {
      items,
      containerWidth,
      getActiveLink,
      renderLink,
      renderHiddenLink,
      renderMoreBtn,
      defaultItemsCount,
    },
    state
  );

  // Refs reset
  visibleItemsRefs.current = new Map();
  hiddenItemsRefs.current = new Map();

  const commonProps = {
    state,
    props: {
      items,
      containerWidth,
      getActiveLink,
      renderLink,
      renderHiddenLink,
      renderMoreBtn,
      defaultItemsCount,
    },
    active: activeLink,
  };

  return (
    <Links ref={listRef}>
      {state.visibleItems.map((item, index) => {
        const ref = React.createRef();
        visibleItemsRefs.current.set(index, ref);

        return renderLink({
          ...commonProps,
          item,
          ref,
          index,
        });
      })}

      {state.hiddenItems.length > 0 && (
        <Select
          onMouseEnter={() => toggle(true)}
          onMouseLeave={() => toggle(false)}
          open={state.isOpen}
        >
          {renderMoreBtn({
            ...commonProps,
            ref: moreLinkRef,
            toggle,
          })}
          <HiddenList isOpen={state.isOpen}>
            {state.hiddenItems.map((item, index) => {
              const ref = React.createRef();
              hiddenItemsRefs.current.set(index, ref);

              return renderHiddenLink({
                ...commonProps,
                item,
                ref,
                index,
              });
            })}
          </HiddenList>
        </Select>
      )}
    </Links>
  );
}

ResponsiveMenu.propTypes = {
  items: PropTypes.oneOfType([
    PropTypes.objectOf(
      PropTypes.shape({
        name: PropTypes.string,
        link: PropTypes.string,
      })
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        link: PropTypes.string,
      })
    ),
  ]).isRequired,
  containerWidth: PropTypes.number,
  getActiveLink: PropTypes.func,
  renderLink: PropTypes.func.isRequired,
  renderHiddenLink: PropTypes.func.isRequired,
  renderMoreBtn: PropTypes.func.isRequired,
  defaultItemsCount: PropTypes.number,
};

export default ResponsiveMenu;
